import React from 'react';
import { createPortal } from 'react-dom';
import { FORM_CONFIG } from '../../../config';

const TOPBAR_SIGNUP_ROLE_OPTIONS = [
  { key: FORM_CONFIG.USER_TYPES.USER, icon: '👤', short: 'User', detail: 'Book services' },
  { key: FORM_CONFIG.USER_TYPES.PANDIT, icon: '🕉️', short: 'Pandit', detail: 'Provide services' },
  { key: FORM_CONFIG.USER_TYPES.EVENT_PARTNER, icon: '🎉', short: 'Event Partner', detail: 'Manage events' },
  { key: FORM_CONFIG.USER_TYPES.SERVICE_PROVIDER, icon: '🛠️', short: 'Provider', detail: 'Offer add-ons' }
];

const TopBarAuthModal = ({
  isOpen,
  mode,
  error,
  loading,
  selectedUserType,
  onUserTypeChange,
  onClose,
  onSwitchMode,
  onLoginSubmit,
  onSignupSubmit
}) => {
  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center bg-black bg-opacity-40 backdrop-blur-sm overflow-y-auto py-4"
      style={{ backdropFilter: 'blur(4px)', zIndex: 10000, position: 'fixed' }}
    >
      <div className="bg-white rounded-3xl shadow-xl max-w-md w-full mx-4 my-4 border border-gray-200 max-h-[calc(100vh-2rem)] overflow-y-auto">
        <div className="relative bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 rounded-t-3xl px-6 py-6">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-white/90 hover:text-white hover:bg-white/20 transition-all duration-200 z-10"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="flex items-center justify-center mb-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/30">
              <img
                src="/themaithil_logo.png"
                alt="TheMaithil logo"
                className="w-8 h-8 object-contain"
              />
            </div>
          </div>

          <div className="text-center text-white space-y-2">
            <h2 className="text-2xl font-bold tracking-wide">
              {mode === 'login' ? 'Welcome Back' : 'Join Us'}
            </h2>
            <p className="text-white/90 text-sm font-medium">
              {mode === 'login' ? 'Continue your spiritual journey' : 'Begin your spiritual journey'}
            </p>
            <div className="w-16 h-0.5 bg-white/40 mx-auto rounded-full"></div>
          </div>
        </div>

        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {mode === 'login' ? (
            <form className="space-y-4" onSubmit={(event) => {
              event.preventDefault();
              onLoginSubmit(new FormData(event.target));
            }}>
              <div className="space-y-1">
                <label className="block text-sm font-semibold text-gray-700">Email Address</label>
                <input
                  name="email"
                  type="email"
                  required
                  disabled={loading}
                  className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-xl"
                  placeholder="Enter your email address"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-semibold text-gray-700">Password</label>
                <input
                  name="password"
                  type="password"
                  required
                  disabled={loading}
                  className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-xl"
                  placeholder="Enter your password"
                />
              </div>

              <label className="flex items-center group cursor-pointer">
                <input name="rememberMe" type="checkbox" className="w-4 h-4" />
                <span className="ml-3 text-sm text-gray-600">Remember me</span>
              </label>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-6 text-sm font-semibold text-white bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 rounded-xl"
              >
                {loading ? 'Signing In...' : 'Sign In to PoojaBooking'}
              </button>

              <div className="text-center pt-3">
                <p className="text-sm text-gray-600">
                  Don't have an account?{' '}
                  <button
                    type="button"
                    onClick={() => onSwitchMode('signup')}
                    className="font-semibold text-orange-600 hover:text-orange-700 hover:underline"
                  >
                    Create account
                  </button>
                </p>
              </div>
            </form>
          ) : (
            <form className="space-y-4" onSubmit={(event) => {
              event.preventDefault();
              onSignupSubmit(new FormData(event.target));
            }}>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Choose your role:</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {TOPBAR_SIGNUP_ROLE_OPTIONS.map((option) => (
                    <label
                      key={option.key}
                      className={`relative flex cursor-pointer rounded-xl border-2 p-3 transition-all duration-200 ${
                        selectedUserType === option.key
                          ? 'border-orange-300 bg-gradient-to-br from-orange-50 to-orange-100 ring-1 ring-orange-200'
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      <input
                        type="radio"
                        name="userType"
                        value={option.key}
                        checked={selectedUserType === option.key}
                        onChange={(event) => onUserTypeChange(event.target.value)}
                        className="sr-only"
                        disabled={loading}
                      />
                      <div className="flex flex-col items-center text-center w-full">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-1 ${
                          selectedUserType === option.key ? 'bg-orange-500' : 'bg-gray-400'
                        }`}>
                          <span className="text-lg text-white">{option.icon}</span>
                        </div>
                        <span className="font-semibold text-gray-900 text-sm">{option.short}</span>
                        <p className="text-xs text-gray-600">{option.detail}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-semibold text-gray-700">Full Name</label>
                <input
                  name="fullName"
                  type="text"
                  required
                  disabled={loading}
                  className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-xl"
                  placeholder="Enter your full name"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-semibold text-gray-700">Email</label>
                <input
                  name="email"
                  type="email"
                  required
                  disabled={loading}
                  className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-xl"
                  placeholder="your@email.com"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-semibold text-gray-700">Phone (Optional)</label>
                <input
                  name="phone"
                  type="tel"
                  disabled={loading}
                  className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-xl"
                  placeholder="10-digit number"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-semibold text-gray-700">Password</label>
                <input
                  name="password"
                  type="password"
                  required
                  disabled={loading}
                  className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-xl"
                  minLength="8"
                  placeholder="Create password"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-semibold text-gray-700">Confirm Password</label>
                <input
                  name="confirmPassword"
                  type="password"
                  required
                  disabled={loading}
                  className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-xl"
                  placeholder="Confirm password"
                />
              </div>

              <label className="flex items-start group cursor-pointer">
                <input
                  name="termsAccepted"
                  type="checkbox"
                  required
                  disabled={loading}
                  className="w-4 h-4 mt-1"
                />
                <span className="ml-3 text-sm text-gray-600">I agree to the Terms of Service and Privacy Policy</span>
              </label>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-6 text-sm font-semibold text-white bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 rounded-xl"
              >
                {loading ? 'Creating Account...' : 'Create Your Account'}
              </button>

              <div className="text-center pt-3">
                <p className="text-sm text-gray-600">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => onSwitchMode('login')}
                    className="font-semibold text-orange-600 hover:text-orange-700 hover:underline"
                  >
                    Sign in here
                  </button>
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TopBarAuthModal;
